// First input delay polyfill for Firebase Performance Monitoring
import 'first-input-delay';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, persistOptions } from './lib/queryClient';
import { AuthProvider } from './context/AuthContext';
import { AudioProvider } from './context/AudioContext';
import { ModerationProvider } from './context/ModerationContext';
import { DMProvider } from './context/DMContext';
import { LeadersChatProvider } from './context/LeadersChatContext';
import { EncryptionProvider } from './context/EncryptionContext';
import { PendingMessagesProvider } from './context/PendingMessagesContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { ToastProvider } from './context/ToastContext';
import { KeyboardProvider } from './context/KeyboardContext';
import { ScrollPositionProvider } from './context/ScrollPositionContext';
import { ConnectionStatusProvider } from './context/ConnectionStatusContext';
import { ErrorBoundary } from './components/errors/ErrorBoundary';
import { AppRecoveryScreen } from './components/errors/AppRecoveryScreen';
import { initializePushNotifications } from './services/pushNotifications';
import { clearChunkReloadFlag } from './utils/lazyWithRetry';
import App from './App';
import './index.css';

// App loaded successfully — clear the chunk-reload guard so future
// deployments can trigger a reload if needed
clearChunkReloadFlag();

// Remove static crawler-only links once React is running
['static-privacy-link', 'static-legal-link'].forEach((id) => {
  document.getElementById(id)?.remove();
});

// Initialize push notifications service worker for users who have already subscribed
// This ensures background notifications work even after app reload
initializePushNotifications().catch(console.error);

// Global handler for unhandled promise rejections.
// React ErrorBoundary only catches synchronous render errors — async errors
// (failed dynamic imports, Firebase timeouts, etc.) bypass it entirely and can
// cause silent white screens. This handler provides a last-resort recovery UI.
window.addEventListener('unhandledrejection', (event) => {
  console.error('[Global] Unhandled promise rejection:', event.reason);

  // Check if the app has rendered any visible content
  const root = document.getElementById('root');
  const hasContent = root && root.children.length > 0 && root.innerHTML.trim().length > 0;

  if (!hasContent) {
    // App hasn't rendered — show recovery UI
    console.warn('[Global] App appears blank after unhandled rejection, showing recovery UI');
    showBlankScreenRecovery('An unexpected error occurred while loading.');
  }
});

// Global handler for uncaught errors (script load failures, etc.)
window.addEventListener('error', (event) => {
  // Check for chunk/module load failures (common after deployments or PWA updates).
  // Different browsers use different error messages:
  //   Chrome:  "Failed to fetch dynamically imported module: ..."
  //   Firefox: "error loading dynamically imported module: ..."
  //   Safari:  "Importing a module script failed"
  //   Vite:    "Loading chunk ... failed"
  const msg = event.message?.toLowerCase() ?? '';
  const isChunkError =
    msg.includes('loading chunk') ||
    msg.includes('dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('failed to load module script') ||
    msg.includes('disallowed mime type');

  if (isChunkError) {
    console.error('[Global] Chunk load failure detected:', event.message);
    showBlankScreenRecovery('A newer version is available. Please reload.');
  }
});

/**
 * Show a recovery UI when the app fails to render.
 * This replaces the root element content with a simple reload prompt.
 */
function showBlankScreenRecovery(message: string): void {
  // Hide the preloader if visible
  const preloader = document.getElementById('spirit-preloader');
  if (preloader) {
    preloader.style.display = 'none';
  }

  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui;background:linear-gradient(135deg,#fcf4fc 0%,#f8e8f8 50%,#f0d2f0 100%);">
        <div style="text-align:center;max-width:400px;padding:2rem;">
          <div style="font-size:2.5rem;margin-bottom:1rem;">⚠️</div>
          <h1 style="color:#63345e;margin-bottom:0.5rem;font-size:1.25rem;">Something went wrong</h1>
          <p style="color:#666;margin-bottom:1.5rem;font-size:0.95rem;">${message}</p>
          <button onclick="window.location.reload()" style="background:#8e4585;color:white;border:none;padding:0.75rem 1.5rem;border-radius:0.5rem;cursor:pointer;font-weight:bold;font-size:1rem;">
            Reload App
          </button>
        </div>
      </div>
    `;
  }
}

// Guard root element - show static error if React can't mount
const rootElement = document.getElementById('root');
if (!rootElement) {
  document.body.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui;background:linear-gradient(135deg,#fcf4fc 0%,#f8e8f8 50%,#f0d2f0 100%);">
      <div style="text-align:center;max-width:400px;padding:2rem;">
        <h1 style="color:#63345e;margin-bottom:0.5rem;">Unable to load application</h1>
        <p style="color:#666;margin-bottom:1.5rem;">Please refresh the page or try again later.</p>
        <button onclick="window.location.reload()" style="background:#8e4585;color:white;border:none;padding:0.75rem 1.5rem;border-radius:0.5rem;cursor:pointer;font-weight:bold;">
          Refresh Page
        </button>
      </div>
    </div>
  `;
  throw new Error('Root element not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary fallback={(error, reset) => <AppRecoveryScreen error={error} onRetry={reset} />}>
      <ConnectionStatusProvider>
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          <BrowserRouter>
            <ScrollPositionProvider>
              <KeyboardProvider>
                <AuthProvider>
                  <EncryptionProvider>
                    <AudioProvider>
                      <ModerationProvider>
                        <ToastProvider>
                          <DMProvider>
                            <LeadersChatProvider>
                              <PendingMessagesProvider>
                                <FavoritesProvider>
                                  <App />
                                </FavoritesProvider>
                              </PendingMessagesProvider>
                            </LeadersChatProvider>
                          </DMProvider>
                        </ToastProvider>
                      </ModerationProvider>
                    </AudioProvider>
                  </EncryptionProvider>
                </AuthProvider>
              </KeyboardProvider>
            </ScrollPositionProvider>
          </BrowserRouter>
        </PersistQueryClientProvider>
      </ConnectionStatusProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
